import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ShieldCheck, AlarmClock, Trash2, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function AdminGdpr() {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/gdpr/status");
    setStatus(data);
  };

  useEffect(() => {
    load();
  }, []);

  const run = async () => {
    if (!confirm("Cleanup nu starten? Documenten + raw content van klanten ouder dan de bewaartermijn worden verwijderd.")) return;
    setRunning(true);
    try {
      const { data } = await api.post("/admin/gdpr/run", {});
      toast.success(`Cleanup voltooid: ${data.result?.expired ?? 0} soft-expired, ${data.result?.hard_deleted ?? 0} hard-deleted.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup mislukt");
    } finally {
      setRunning(false);
    }
  };

  if (!status) return <div className="text-oc-textSecondary">Bezig met laden...</div>;

  return (
    <div className="space-y-7">
      <div>
        <h1 className="font-display font-semibold text-oc-primary text-[34px] leading-tight flex items-center gap-3">
          <ShieldCheck className="w-8 h-8 text-oc-fair" /> GDPR cleanup
        </h1>
        <p className="text-oc-textSecondary text-[14px]">Automatische verwijdering van klantdocumenten na {status.retention_days} dagen — extra controle hieronder.</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="bg-white border border-oc-border rounded-oc p-6">
          <p className="text-[11px] uppercase tracking-wide font-semibold text-oc-textSecondary">Bewaartermijn</p>
          <p className="font-mono font-bold text-[32px] text-oc-primary mt-1">{status.retention_days}</p>
          <p className="text-[12px] text-oc-textSecondary">dagen</p>
        </div>
        <div className="bg-white border border-oc-border rounded-oc p-6">
          <p className="text-[11px] uppercase tracking-wide font-semibold text-oc-textSecondary">Klaar voor verwijdering</p>
          <p data-testid="gdpr-due-now" className="font-mono font-bold text-[32px] text-oc-excessive mt-1">{status.due_now}</p>
          <p className="text-[12px] text-oc-textSecondary">records met expires_at &lt; nu</p>
        </div>
        <div className="bg-white border border-oc-border rounded-oc p-6">
          <p className="text-[11px] uppercase tracking-wide font-semibold text-oc-textSecondary">Reeds verwijderd</p>
          <p className="font-mono font-bold text-[32px] text-oc-primary mt-1">{status.already_expired}</p>
          <p className="text-[12px] text-oc-textSecondary">soft-deleted records</p>
        </div>
      </div>

      <div className="bg-white border border-oc-border rounded-oc p-7 space-y-4">
        <div className="flex items-center gap-3">
          <AlarmClock className="w-5 h-5 text-oc-secondary" />
          <p className="text-[14px]">
            Laatste run: <strong>{status.last_run_iso ? new Date(status.last_run_iso).toLocaleString("nl-BE") : "nog niet gedraaid"}</strong> · Volgende: dagelijks om 02:00 (Europe/Brussels)
          </p>
        </div>
        <Button
          onClick={run}
          disabled={running}
          data-testid="run-gdpr-now"
          className="rounded-oc-btn bg-oc-excessive hover:bg-oc-excessive/90 text-white"
        >
          <PlayCircle className="w-4 h-4 mr-2" /> {running ? "Bezig..." : "Cleanup nu uitvoeren"}
        </Button>
      </div>

      {(status.next_due_sample || []).length > 0 && (
        <div className="bg-white border border-oc-border rounded-oc p-7">
          <p className="text-[13px] font-semibold text-oc-primary flex items-center gap-2">
            <Trash2 className="w-4 h-4" /> Eerste 5 records die bij de volgende run verwijderd worden:
          </p>
          <div className="mt-3 space-y-2">
            {status.next_due_sample.map((r) => (
              <div key={r.id} className="flex items-center justify-between text-[13px] font-mono bg-oc-subtle rounded-oc-btn px-4 py-2">
                <span>{r.id}</span>
                <span className="text-oc-textSecondary">{r.email}</span>
                <span className="text-oc-excessive">{r.expires_at_iso?.slice(0, 16).replace("T", " ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
